import React, { useState, useCallback } from 'react';
import PropTypes from 'prop-types';
import { Icon } from '@ontruck/orc';
import useSWR from 'swr';
import { fetcher } from 'utils/network/http';
import DocumentsUpload from 'components/DocumentsUpload';
import config from 'utils/constants/config';
import { VEHICLE_ICONS } from 'utils/constants/vehicles';
import { useTranslation } from 'react-i18next';
import cx from 'classnames';

const DocumentsList = ({
  documents,
  filterForPending,
  refetchDocuments,
  entity_type,
  entity_uuid,
}) => {
  if (!documents) return null;
  return documents
    .filter(filterForPending)
    .map((document) => (
      <DocumentsUpload
        document={document}
        entity_type={entity_type}
        uuid={entity_uuid}
        refetchDocuments={refetchDocuments}
        key={document.id}
      />
    ));
};

const DocumentsError = () => {
  const { t } = useTranslation('Documents');
  return (
    <div
      className="documents-accordion__error"
      data-testid="documents-accordion__error"
    >
      {t('errorGettingDocuments')}
    </div>
  );
};

const DocumentsLoading = () => {
  return (
    <div
      className="documents-accordion__loading"
      data-testid="documents-accordion__loading"
    >
      ◠
    </div>
  );
};

const DocumentsAccordion = ({
  className,
  item,
  request,
  areTermsAccepted,
  isStaff,
  filterForPending,
}) => {
  const { t } = useTranslation('Documents');
  const [show, setShow] = useState(false);

  const { data: documentsResponse, error, mutate: refetchDocuments } = useSWR(
    item?.entity_uuid && (areTermsAccepted || isStaff) && show
      ? request(item.entity_uuid)
      : null,
    fetcher,
    {
      refreshInterval: config.SLOW_POLLING_DELAY,
    }
  );
  const documents = documentsResponse?.data;
  const handleAccordion = useCallback(() => {
    setShow(!show);
  }, [show]);
  const hasPendingDocs = item.not_uploaded_count > 0;
  return (
    <div className={className} data-testid="documents-accordion">
      <div className="documents-accordion">
        <button
          className="documents-accordion__header"
          onClick={handleAccordion}
          data-testid="documents-accordion__header"
        >
          <div className="documents-accordion__header-name">
            {item.entity_type === 'vehicle' && (
              <div className="documents-accordion__vehicle-icon">
                <Icon glyph={VEHICLE_ICONS[item.vehicle_type]} size="3rem" />
              </div>
            )}
            <span className="documents-accordion__header-title">
              {item.name}
            </span>
          </div>
          {/*We are going to show this tag in the next iteration */}
          {false && (
            <div className="documents-accordion__status-container">
              <span
                className={cx(
                  'documents-accordion__status',
                  { 'documents-accordion__status--pending': hasPendingDocs },
                  { 'documents-accordion__status--completed': !hasPendingDocs }
                )}
              >
                {hasPendingDocs
                  ? t('pendingDocuments')
                  : t('completedDocuments')}
              </span>
            </div>
          )}
          <span className="documents-accordion__label">
            <span className="documents-accordion__label-text">
              {show ? t('hideDocuments') : t('seeDocuments')}
            </span>
            <span
              className={cx('documents-accordion__label-icon', {
                'documents-accordion__label-icon--open': show,
              })}
            >
              {(documentsResponse || !show) && (
                <Icon glyph="continue" size="1rem" />
              )}
              {!documentsResponse && show && <DocumentsLoading />}
            </span>
          </span>
        </button>
        {show && documentsResponse && (
          <div className="documents-accordion__container">
            {error && <DocumentsError />}
            <div className="documents-accordion__sublist-columns">
              <span className="documents-accordion__column documents-accordion__column--expire">
                {t('expire')}
              </span>
              <span className="documents-accordion__column documents-accordion__column--state">
                {t('state')}
              </span>
            </div>
            <DocumentsList
              documents={documents}
              filterForPending={filterForPending}
              refetchDocuments={refetchDocuments}
              entity_type={item.entity_type}
              entity_uuid={item.entity_uuid}
            />
          </div>
        )}
      </div>
    </div>
  );
};

DocumentsAccordion.propTypes = {
  className: PropTypes.string,
  item: PropTypes.object.isRequired,
};

export default DocumentsAccordion;
